import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { sendFirstMessageAgent, sendFirstMessageCustomChat, sendFirstMessageOffer } from '../../actions/firstMessage';
import { agentIdSelector, customChatParamsSelector, currentOpenChatSelector, offerSelector } from '../../selectors';
import { EFirstMessageStatus } from '../../types/firstMessageStatus';

interface ISavedFirstMessage {
  /** Текст первого сообщения */
  text: string;
  /** Пэйлоад первого сообщения */
  payload?: string | null;
}

/**
 * Отправляет первое сообщение (по объявлению, агенту или кастомной сущности) и повторяет отправку после прохождения капчи.
 */
export function useSendFirstMessage() {
  const dispatch = useDispatch();

  const offer = useSelector(offerSelector);
  const agentId = useSelector(agentIdSelector);
  const customChatParams = useSelector(customChatParamsSelector);
  const currentChat = useSelector(currentOpenChatSelector);

  const { captchaToken, firstMessageStatus } = currentChat || /* istanbul ignore next */ {};
  const { id: offerId } = offer || {};

  const [savedFirstMessage, setSavedFirstMessage] = React.useState<ISavedFirstMessage | null>(null);

  const canSendFirstMessage = Boolean(offerId || agentId || customChatParams);
  const isFirstMessageInProgress =
    firstMessageStatus === EFirstMessageStatus.Pending || firstMessageStatus === EFirstMessageStatus.NeedCaptcha;

  /**
   * Отправляет первое сообщение и сохраняет его для повтора после капчи.
   */
  const sendFirstMessage = React.useCallback(
    ({ text, payload }: ISavedFirstMessage) => {
      if (offerId) {
        dispatch(sendFirstMessageOffer({ text, offerId, captchaToken, payload }));
      } else if (agentId) {
        dispatch(sendFirstMessageAgent({ text, captchaToken, agentId }));
      } else if (customChatParams) {
        dispatch(sendFirstMessageCustomChat({ text, captchaToken, customChatParams }));
      } else {
        return;
      }

      setSavedFirstMessage({ text, payload });
    },
    [dispatch, offerId, agentId, customChatParams, captchaToken],
  );

  // Повторяет отправку сохранённого сообщения после прохождения капчи.
  React.useEffect(() => {
    if (!savedFirstMessage || firstMessageStatus !== EFirstMessageStatus.NeedCaptcha || !captchaToken) {
      return;
    }

    sendFirstMessage(savedFirstMessage);
  }, [captchaToken, firstMessageStatus, savedFirstMessage, sendFirstMessage]);

  return { canSendFirstMessage, isFirstMessageInProgress, sendFirstMessage };
}
